import React, { useState } from 'react'
import { Save, RefreshCw, Database, Key, Bell, Shield } from 'lucide-react'

const Settings: React.FC = () => {
  const [settings, setSettings] = useState({
    // 系统设置
    systemName: 'ACC AI助手',
    systemDescription: '专业的针灸穴位识别系统',
    maintenanceMode: false,
    
    // AI设置
    aiModel: 'gemini-pro-vision',
    confidenceThreshold: 0.7,
    maxPredictions: 10,
    
    // 通知设置
    emailNotifications: true,
    smsNotifications: false,
    webhookUrl: '',
    
    // 安全设置
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    requireTwoFactor: false,
    
    // 数据库设置
    backupFrequency: 'daily',
    retentionDays: 90,
    autoCleanup: true
  })

  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  const handleSave = async () => {
    try {
      setLoading(true)
      // 这里应该调用实际的API
      // await fetch('/api/admin/settings', {
      //   method: 'PUT',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(settings)
      // })
      
      // 模拟保存
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      setMessage({ type: 'success', text: '设置已保存' })
      setTimeout(() => setMessage(null), 3000)
    } catch (error) {
      setMessage({ type: 'error', text: '保存失败，请重试' })
      setTimeout(() => setMessage(null), 3000)
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    if (confirm('确定要重置所有设置吗？此操作不可撤销。')) {
      // 重置到默认值
      setSettings({
        systemName: 'ACC AI助手',
        systemDescription: '专业的针灸穴位识别系统',
        maintenanceMode: false,
        aiModel: 'gemini-pro-vision',
        confidenceThreshold: 0.7,
        maxPredictions: 10,
        emailNotifications: true,
        smsNotifications: false,
        webhookUrl: '',
        sessionTimeout: 30,
        maxLoginAttempts: 5,
        requireTwoFactor: false,
        backupFrequency: 'daily',
        retentionDays: 90,
        autoCleanup: true
      })
      setMessage({ type: 'success', text: '设置已重置' })
      setTimeout(() => setMessage(null), 3000)
    }
  }

  const updateSetting = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">系统设置</h1>
          <p className="text-gray-500">配置系统参数和功能选项</p>
        </div>
        <div className="flex space-x-3">
          <button onClick={handleReset} className="btn-secondary">
            <RefreshCw className="h-4 w-4 mr-2" />
            重置设置
          </button>
          <button onClick={handleSave} disabled={loading} className="btn-primary">
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            保存设置
          </button>
        </div>
      </div>

      {/* 消息提示 */}
      {message && (
        <div className={`p-4 rounded-md ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* 设置面板 */}
      <div className="grid grid-cols-1 gap-6">
        {/* 基本设置 */}
        <div className="card p-6">
          <div className="flex items-center mb-4">
            <Shield className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">基本设置</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">系统名称</label>
              <input
                type="text"
                className="input"
                value={settings.systemName}
                onChange={(e) => updateSetting('systemName', e.target.value)}
              />
            </div>
            <div>
              <label className="label">系统描述</label>
              <input
                type="text"
                className="input"
                value={settings.systemDescription}
                onChange={(e) => updateSetting('systemDescription', e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="maintenanceMode"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  checked={settings.maintenanceMode}
                  onChange={(e) => updateSetting('maintenanceMode', e.target.checked)}
                />
                <label htmlFor="maintenanceMode" className="ml-2 text-sm text-gray-700">
                  启用维护模式（将暂停所有用户访问）
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* AI设置 */}
        <div className="card p-6">
          <div className="flex items-center mb-4">
            <Key className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">AI设置</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">AI模型</label>
              <select
                className="input"
                value={settings.aiModel}
                onChange={(e) => updateSetting('aiModel', e.target.value)}
              >
                <option value="gemini-pro-vision">Gemini Pro Vision</option>
                <option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
                <option value="gemini-1.5-flash">Gemini 1.5 Flash</option>
              </select>
            </div>
            <div>
              <label className="label">置信度阈值</label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.1"
                className="input"
                value={settings.confidenceThreshold}
                onChange={(e) => updateSetting('confidenceThreshold', parseFloat(e.target.value))}
              />
            </div>
            <div>
              <label className="label">最大预测数量</label>
              <input
                type="number"
                min="1"
                max="20"
                className="input"
                value={settings.maxPredictions}
                onChange={(e) => updateSetting('maxPredictions', parseInt(e.target.value))}
              />
            </div>
          </div>
        </div>

        {/* 通知设置 */}
        <div className="card p-6">
          <div className="flex items-center mb-4">
            <Bell className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">通知设置</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="emailNotifications"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={settings.emailNotifications}
                onChange={(e) => updateSetting('emailNotifications', e.target.checked)}
              />
              <label htmlFor="emailNotifications" className="ml-2 text-sm text-gray-700">
                启用邮件通知
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="smsNotifications"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={settings.smsNotifications}
                onChange={(e) => updateSetting('smsNotifications', e.target.checked)}
              />
              <label htmlFor="smsNotifications" className="ml-2 text-sm text-gray-700">
                启用短信通知
              </label>
            </div>
            <div>
              <label className="label">Webhook URL</label>
              <input
                type="url"
                className="input"
                placeholder="https://example.com/webhook"
                value={settings.webhookUrl}
                onChange={(e) => updateSetting('webhookUrl', e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">用于接收系统事件通知</p>
            </div>
          </div>
        </div>

        {/* 安全设置 */}
        <div className="card p-6">
          <div className="flex items-center mb-4">
            <Shield className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">安全设置</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">会话超时时间（分钟）</label>
              <input
                type="number"
                min="5"
                max="120"
                className="input"
                value={settings.sessionTimeout}
                onChange={(e) => updateSetting('sessionTimeout', parseInt(e.target.value))}
              />
            </div>
            <div>
              <label className="label">最大登录尝试次数</label>
              <input
                type="number"
                min="3"
                max="10"
                className="input"
                value={settings.maxLoginAttempts}
                onChange={(e) => updateSetting('maxLoginAttempts', parseInt(e.target.value))}
              />
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requireTwoFactor"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  checked={settings.requireTwoFactor}
                  onChange={(e) => updateSetting('requireTwoFactor', e.target.checked)}
                />
                <label htmlFor="requireTwoFactor" className="ml-2 text-sm text-gray-700">
                  要求双因素认证
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* 数据库设置 */}
        <div className="card p-6">
          <div className="flex items-center mb-4">
            <Database className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">数据库设置</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">备份频率</label>
              <select
                className="input"
                value={settings.backupFrequency}
                onChange={(e) => updateSetting('backupFrequency', e.target.value)}
              >
                <option value="hourly">每小时</option>
                <option value="daily">每天</option>
                <option value="weekly">每周</option>
                <option value="monthly">每月</option>
              </select>
            </div>
            <div>
              <label className="label">数据保留天数</label>
              <input
                type="number"
                min="30"
                max="365"
                className="input"
                value={settings.retentionDays}
                onChange={(e) => updateSetting('retentionDays', parseInt(e.target.value))}
              />
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="autoCleanup"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  checked={settings.autoCleanup}
                  onChange={(e) => updateSetting('autoCleanup', e.target.checked)}
                />
                <label htmlFor="autoCleanup" className="ml-2 text-sm text-gray-700">
                  启用自动清理过期数据
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Settings